import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowRight, Search, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import axios from 'axios';
import PageTitle from '../hooks/PageTitle';
import InsightsSkeletonLoader from '../components/skeletons/InsightsSkeletonLoader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Insights = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/insights/`);
      setBlogPosts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching insights:', err);
      setError('Unable to load articles. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...new Set(blogPosts.map(post => post.category).filter(Boolean))];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = filteredPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  if (loading) {
    return <InsightsSkeletonLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
      <PageTitle title="Insights & Perspectives" />
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800">{error}</p>
            <button 
              onClick={fetchInsights}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="Insights & Perspectives" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-serif mb-6">
              Insights & Perspectives
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Expert analysis and thought leadership on corporate law, international transactions, and regulatory developments
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-600">Prova a modificare i filtri di ricerca</p>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featuredPost && (
                <div className="mb-16">
                  <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <div className="md:flex">
                      <div className="md:flex-shrink-0">
                        {featuredPost.image_url ? (
                          <img 
                            className="h-64 w-full md:w-96 object-cover" 
                            src={featuredPost.image_url} 
                            alt={featuredPost.title} 
                          />
                        ) : (
                          <div className="h-64 w-full md:w-96 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                            <FileText className="h-24 w-24 text-white opacity-50" />
                          </div>
                        )}
                      </div>
                      <CardContent className="p-8">
                        <div className="flex items-center gap-3 mb-4">
                          <Badge className="bg-gray-900">{featuredPost.category}</Badge>
                        </div>
                        <h2 className="text-3xl font-serif text-gray-900 mb-4 hover:text-gray-700 transition-colors">
                          {featuredPost.title}
                        </h2>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                          {featuredPost.excerpt}
                        </p>
                        <div className="flex items-center gap-6 text-sm text-gray-500 mb-6">
                          <div className="flex items-center">
                            <User className="h-4 w-4 mr-2" />
                            {featuredPost.author}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2" />
                            {new Date(featuredPost.published_at).toLocaleDateString('it-IT')}
                          </div>
                        </div>
                        <Link 
                          to={`/insights/${featuredPost.id || featuredPost._id}`}
                          className="inline-flex items-center text-gray-900 font-medium hover:gap-3 transition-all group"
                        >
                          Read Article
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </CardContent>
                    </div>
                  </Card>
                </div>
              )}

              {/* Regular Posts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularPosts.map((post) => (
                  <Card key={post.id || post._id} className="group hover:shadow-lg transition-all duration-300 overflow-hidden">
                    {post.image_url ? (
                      <img 
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" 
                        src={post.image_url} 
                        alt={post.title} 
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                        <FileText className="h-16 w-16 text-white opacity-50" />
                      </div>
                    )}
                    <CardContent className="p-6">
                      {post.category && (
                        <Badge className="mb-3">{post.category}</Badge>
                      )}
                      <h3 className="text-xl font-serif text-gray-900 mb-3 group-hover:text-gray-700 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          {post.author}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(post.published_at).toLocaleDateString('it-IT')}
                        </div>
                      </div>
                      <Link 
                        to={`/insights/${post.id || post._id}`}
                        className="mt-4 inline-flex items-center text-sm text-gray-900 font-medium hover:gap-2 transition-all group/btn"
                      >
                        Read More
                        <ArrowRight className="ml-1 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Insights;
